import { ContentObserver } from '@angular/cdk/observers';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { isNullish } from '@epicuro-next/utilities';
import { Product } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import * as Sortable from 'sortablejs';

export type ProductReferences = ReadonlyArray<{ id: string }>;

const MEDIA_CLASS = 'media-wrapper';
const TARGET_MEDIA_RATIO = 0.75; // taken from current average p-card sizes
const RATIO_DIVISION_ERR_APPROXIMATE = 0.01;

@Component({
  selector: 'epc-dynamic-elements-fluid-draggable-list-content',
  templateUrl: './fluid-dragable-list-content.component.html',
  styleUrls: ['./fluid-dragable-list-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FluidDragableListContentComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  public set startWithRatio(value: number) {
    this.definedAspectRatio$$.next(value);
  }

  @Input()
  public groupedByCategory = false;

  /**
   * Enables ngx-sortablejs directive root
   */
  @Input()
  public sortable = false;

  /**
   * Not used to render products.
   * It's used to track and reorganize dragable items.
   * The array order will be modified by ngx-sortable.
   * It's only required in draggable mode
   */
  @Input()
  public set productReferences(value: Product[]) {
    this.createItems((value ?? []).map((item) => ({ id: item.productId })));
  }

  @Output()
  public productSorted = new EventEmitter<{
    id: string;
    newIndex: number;
  }>();

  @ViewChild('mediaWrapper')
  public mediaWrapper: ElementRef | null = null;

  public sortableOptions?: Sortable.Options = {};
  public items?: ({ id: string } | null)[];

  public trueMediaAspectRatio$$ = new BehaviorSubject(0.51);
  public containsMedia$ = new BehaviorSubject(false);
  private definedAspectRatio$$ = new BehaviorSubject(0.51);
  private destroy$$ = new Subject<void>();

  public mediaSizeClass$ = merge(
    this.trueMediaAspectRatio$$.pipe(debounceTime(200)),
    this.definedAspectRatio$$,
  ).pipe(
    distinctUntilChanged(),
    map((ratio) => ratio / TARGET_MEDIA_RATIO - RATIO_DIVISION_ERR_APPROXIMATE),
    map((ratio) => {
      if (ratio > 3) return 'quadruple-media';
      if (ratio > 2) return 'triple-media';
      if (ratio > 1) return 'double-media';
      if (ratio > 0.5) return '';
      return 'double-row-media';
    }),
  );

  constructor(private observer: ContentObserver) {}

  public ngOnInit() {
    this.sortableOptions = this.getSortableJSOptions();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public ngAfterViewInit(): void {
    const element = this.mediaWrapper?.nativeElement;
    this.observer
      .observe(element)
      .pipe(
        startWith(element),
        map(() => ({
          // use this.mediaWrapper?.nativeElement except element variable
          // element can lead to deleted dom at some moment
          img: this.mediaWrapper?.nativeElement.querySelector('img'),
          video: this.mediaWrapper?.nativeElement.querySelector('video'),
          iframe: this.mediaWrapper?.nativeElement.querySelector('iframe'),
        })),
        map(({ img, video, iframe }) => {
          return !!img || !!video || !!iframe;
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe((hasSomething) => {
        this.containsMedia$.next(hasSomething);
      });
  }

  private getSortableJSOptions() {
    return {
      handle: '.dnd-icon-holder',
      onMove: (event: Sortable.MoveEvent) => {
        return validateIfNotOnMediaElement(event.related);
      },
      onUpdate: (event: Sortable.SortableEvent) => {
        const { oldIndex, newIndex } = event;
        if (isNullish(oldIndex) || isNullish(newIndex)) {
          return;
        }
        const item = this.items?.at(newIndex) as { id: string };

        this.productSorted.emit({
          id: item.id,
          newIndex: newIndex - 1,
        });
      },
    } as Sortable.Options;
  }

  private createItems(productReferences: ProductReferences) {
    if (!productReferences || productReferences.length === 0) {
      this.items = undefined;
      return;
    }

    productReferences = sortIdsByItems(
      productReferences,
      this.items as ProductReferences,
    );

    this.items = [null, ...productReferences];
  }
}

function validateIfNotOnMediaElement(related: HTMLElement) {
  return !related.classList.contains(MEDIA_CLASS);
}

export function sortIdsByItems(
  toSort: ProductReferences,
  bySort: ProductReferences,
) {
  const orderMap = new Map<string, number>();
  bySort?.forEach((item, index) => orderMap.set(item?.id ?? 'null', index));
  return toSort.slice().sort((a, b) => {
    const indexA = orderMap.get(a.id);
    const indexB = orderMap.get(b.id);

    if (indexA !== undefined && indexB !== undefined) {
      return indexA - indexB;
    } else if (indexA !== undefined) {
      return -1;
    } else if (indexB !== undefined) {
      return 1;
    } else {
      return 0;
    }
  });
}
